import { useEffect, useMemo, useState } from "react";
import { Cell, useBlockLayout, useFilters, useGlobalFilter, usePagination, useRowSelect, useSortBy, useTable } from "react-table";
import { useApiOperation } from "src/api/hooks";
import { getPartnerMemberConfigListAsync } from "src/api/partner";
import { PartnerMemberConfigListResponse } from "src/api/partner/type";
import { getVocItemReceiversAsync, submitVocItemReceiverAsync } from "src/api/voc/voc-api";
import { CsItemModel } from "src/api/voc/voc-types";
import { BaseToggle } from "src/components/BaseToggle";
import GoToListButton from "src/components/GoToListButton";
import { ArticleSection } from "src/components/layout/ArticleSection";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import { BULLET, formatKoreanPhoneNumber } from "src/utils";
import { CsNotificationChannels } from "../../voc-constants";

const alarmReceiverColumns = [
  {
    Header: "관리자 이메일",
    accessor: "email",
    width: 340,
    Cell: ({ value }: Cell<PartnerList>) => {
      return <div className="flex-center-start w-100">{value || "-"}</div>;
    },
  },
  {
    Header: "휴대폰 번호",
    accessor: "phoneNumber",
    width: 340,
    Cell: ({ value }: Cell<PartnerList>) => {
      return <div className="flex-center-start w-100">{formatKoreanPhoneNumber(value) || "-"}</div>;
    },
  },
  {
    Header: "알림 유형",
    accessor: "visitorApprovalNotificationChannels",
    width: 280,
    Cell: ({ row }: Cell<PartnerList>) => {
      const csNotificationChannels = row.original.csNotificationChannels;
      return (
        <div className="flex-center-start w-100">
          {csNotificationChannels
            ?.sort((a, b) => a.localeCompare(b))
            .map((channelName) => CsNotificationChannels[channelName])
            .join(", ") || "-"}
        </div>
      );
    },
  },
  {
    Header: "알림 설정",
    accessor: "isSend",
    width: 200,
    Cell: (props: any) => {
      const isSend = props.row.original.isSend;
      const row = props.row.original;
      const onSubmitVocItemReceiver = props.onSubmitVocItemReceiver;
      const isAuthority = props.isAuthority;
      const fetchAuthorityReadonly = props.fetchAuthorityReadonly;
      return (
        <div className="flex-center-start w-100">
          <BaseToggle
            className=""
            checked={isSend || false}
            onChange={(checked: boolean) => {
              if (isAuthority("w") === false) {
                fetchAuthorityReadonly(true);
                return;
              }
              onSubmitVocItemReceiver({
                partnerMemberId: row.memberId,
                isSend: checked,
              });
            }}
          />
        </div>
      );
    },
  },
];

type Props = {
  vocDetail: CsItemModel | undefined;
};

type PartnerList = PartnerMemberConfigListResponse;

const VocAlramSetting = ({ vocDetail }: Props) => {
  const [partnerMembers, setPartnerMembers] = useState<PartnerList[]>([]);
  const { isAuthority, fetchAuthorityReadonly } = usePartnerAuthority();

  /** 파트너 회원 설정 목록 조회 */
  const { executeAsync: getPartnerMemberConfigList } = useApiOperation(getPartnerMemberConfigListAsync);

  /** 저장된 알림 수신자 조회 */
  const { executeAsync: getVocItemReceivers } = useApiOperation(getVocItemReceiversAsync);
  const { executeAsync: submitVocItemReceiver } = useApiOperation(submitVocItemReceiverAsync);

  useEffect(() => {
    if (!vocDetail?.id) return;
    fetchPartnerMemberConfigList();
  }, [vocDetail?.id]);

  // 파트너 회원 설정 목록 조회 (테이블 데이터 조회)
  const fetchPartnerMemberConfigList = async () => {
    const { data } = await getPartnerMemberConfigList({
      page: 0,
      size: 100,
      hasConfig: "cs-notification-channels",
      sort: {
        orders: [{ direction: "ASC", property: "email" }],
      },
    });

    const result = data.data.content;
    // 수신자 조회가 실패해도 회원 목록은 보여준다. 이때 토글은 저장된 값이 없을 때와 같게 보인다
    const vocItemReceivers = await fetchVocItemReceivers().catch(() => []);

    const partnerMemberList = result.map((item) => {
      const vocItemReceiver = vocItemReceivers.find((receiver) => receiver.partnerMemberId === item.memberId);
      return {
        ...item,
        isSend: vocItemReceiver ? vocItemReceiver.isSend : true,
      };
    });
    setPartnerMembers(partnerMemberList);
  };

  // 저장된 알림 수신자 조회
  const fetchVocItemReceivers = async () => {
    const { data } = await getVocItemReceivers({ itemId: Number(vocDetail?.id) });
    return data.data.content;
  };

  // 알림 수신자 저장 > Select On OFF
  const onSubmitVocItemReceiver = async (passData: { partnerMemberId: string; isSend: boolean }) => {
    await submitVocItemReceiver({
      itemId: Number(vocDetail?.id),
      partnerMemberId: passData.partnerMemberId,
      isSend: passData.isSend,
    });
    await fetchPartnerMemberConfigList();
  };

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns: alarmReceiverColumns as any,
      data: partnerMembers || [],
      onSubmitVocItemReceiver,
      isAuthority,
      fetchAuthorityReadonly,
    },
    useBlockLayout,

    useRowSelect,
  );

  // columns width 의 총 합 (table witdh 를 구하기 위함)
  const tableWidth = useMemo(() => {
    let totalWidth = 0;
    headerGroups.forEach((headerGroup) => {
      headerGroup.headers.forEach((header) => (totalWidth += Number(header?.width || 0)));
    });
    return totalWidth;
  }, [headerGroups]);

  return (
    <div>
      <div className="contents-container__scroll">
        <div className="contents-container__wrap ">
          <ArticleSection title="알림 수신자">
            <div className="font14 text-primary3">
              <p>
                {BULLET} 현재 VoC 항목 : {vocDetail?.csItemName}
              </p>
              <p>{BULLET} 해당 VoC 항목으로 접수된 신규 CS에 대한 알림 수신 대상을 지정할 수 있습니다.</p>
            </div>

            <div className="mt20">
              <section className="inner-tab-table mb20 contents-container__1200">
                <div {...getTableProps()} className={`base-table sticky`}>
                  <div
                    className="header" //
                    // style={{ borderBottom: "1px solid #E0E0E0" }}
                  >
                    {headerGroups.map((headerGroup) => (
                      <div {...headerGroup.getHeaderGroupProps()} className="base-table__tr">
                        {headerGroup.headers.map((header) => {
                          return (
                            <div {...header.getHeaderProps(undefined)} className={`base-table__th cursor-unset flex-center-start`}>
                              {header.render("Header")}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                  <div {...getTableBodyProps()} className="body max-height500">
                    {rows.map((row: any) => {
                      prepareRow(row);
                      return (
                        <div {...row.getRowProps()} className={`base-table__tr`}>
                          {row.cells.map((cell: any) => {
                            return (
                              <div {...cell.getCellProps()} className={`base-table__td`}>
                                {cell.render("Cell")}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                  {rows.length === 0 && (
                    <div className="base-table__tr table-cursor-unset" style={{ width: tableWidth }}>
                      <div className="base-table__td w-100 text-center">
                        <div className="w-100">
                          <span>등록된 데이터가 없습니다.</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </section>
            </div>
          </ArticleSection>
        </div>
      </div>

      {/* 버튼영역 */}
      <div className="contents-container__btn-wrap">
        <div className="left-area">
          <GoToListButton />
        </div>
      </div>
    </div>
  );
};

export default VocAlramSetting;
